import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, GraduationCap, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { CategoryBadge } from "../components/CategoryBadge";

const EXAMPLES = [
  "Se il cliente mi dice \"richiamami più tardi\", cosa risponderesti?",
  "Come gestisco \"devo parlarne con il mio socio\"?",
  "Il cliente dice che il prezzo è troppo alto: cosa dico?",
  "Come chiudo quando il cliente è indeciso?",
];

const COPERTURA = { completa: ["#34D399", "Copertura completa"], parziale: ["#F59E0B", "Copertura parziale"], assente: ["#F87171", "Nessun contenuto pertinente"] };

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (q) => {
    const domanda = (q ?? input).trim();
    if (domanda.length < 5 || loading) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: "user", text: domanda }]);
    setLoading(true);
    try {
      const { data } = await api.post("/coach/ask", { domanda, history });
      setMessages((m) => [...m, { role: "coach", text: data.risposta, fonti: data.fonti, copertura: data.copertura }]);
    } catch (e) {
      toast.error(errorMessage(e));
      setMessages((m) => m.slice(0, -1));
      setInput(domanda);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="coach-page" className="flex min-h-[70vh] flex-col space-y-6">
      <div className="fade-up flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Helper offline</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Coach</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Interroga la tua Knowledge Base prima di una call: risposte basate solo sui contenuti che hai caricato.</p>
        </div>
        {messages.length > 0 && (
          <button data-testid="coach-reset-btn" onClick={() => setMessages([])} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-white"><RotateCcw className="h-4 w-4" /> Nuova conversazione</button>
        )}
      </div>

      <div data-testid="coach-thread" className="surface flex-1 space-y-5 p-4 sm:p-6">
        {messages.length === 0 && (
          <div className="flex flex-col items-center gap-5 py-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/20 text-primary"><GraduationCap className="h-7 w-7" /></div>
            <p className="max-w-md text-sm text-muted-foreground">Chiedi come rispondere a un'obiezione, come chiudere o come presentare il valore. Prova con:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {EXAMPLES.map((e, i) => (
                <button key={i} data-testid={`coach-example-${i}`} onClick={() => ask(e)} className="rounded-full border border-border px-4 py-2 text-left text-xs text-muted-foreground transition-colors hover:border-primary hover:text-white">{e}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} data-testid="coach-user-msg" className="flex justify-end">
              <p className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-3 text-sm text-white">{m.text}</p>
            </div>
          ) : (
            <div key={i} data-testid="coach-answer" className="max-w-[92%] space-y-3">
              <div className="rounded-2xl rounded-bl-sm border border-[#F5B942]/40 bg-[#F5B942]/10 px-5 py-4">
                <div className="mb-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest" style={{ color: COPERTURA[m.copertura]?.[0] }}><Sparkles className="h-3.5 w-3.5" /> {COPERTURA[m.copertura]?.[1]}</div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p>
              </div>
              {m.fonti?.length > 0 && (
                <div className="space-y-2 pl-2">
                  <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Basato su</p>
                  {m.fonti.map((f) => (
                    <Link key={f.id} to={`/contenuti/${f.content_id}`} data-testid={`coach-source-${f.id}`} className="flex items-start gap-2 rounded-lg p-2 text-xs text-gray-300 transition-colors hover:bg-secondary">
                      <CategoryBadge categoria={f.categoria} /><span className="line-clamp-2">{f.testo}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )
        )}
        {loading && <div data-testid="coach-loading" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Consulto i tuoi contenuti…</div>}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="glass sticky bottom-24 flex items-center gap-2 rounded-full border border-white/10 p-2 md:bottom-4">
        <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Se il cliente mi dice… cosa rispondo?" className="flex-1 bg-transparent px-4 py-2.5 text-sm outline-none" />
        <button data-testid="coach-send-btn" type="submit" disabled={loading || input.trim().length < 5} className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-40"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
